import strings from '../../strings/strings.json';
import { breakpoints, sizes, theme } from 'theme';
import { Course } from 'models/Course';
import ModuleAccordion from 'pages/build-course/components/ModuleAccordion';
import MainSection from 'components/sections/MainSection';
import styled from 'styled-components';
import { BodyRegular, FlexAlignCenter, FlexAlignCenterMediumGap, FlexColMediumGap, Headline3 } from '../Elements';
import { useNavigate } from 'react-router-dom';
import { LearnerCardBase } from 'pages/learner/sharedStyles/LearnerStyles';
import CheckIcon from 'assets/icons/form-icons/CheckIcon';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import ScheduleIcon from 'assets/icons/navigation/ScheduleIcon';
import VisibilityIcon from 'assets/icons/accessibility/VisibilityIcon';
import EditIcon from 'assets/icons/controls/EditIcon';

type CourseOverviewProps = {
    course: Course;
};

export default function CourseOverview({ course }: CourseOverviewProps): JSX.Element {
    const navigate = useNavigate();

    const previewCourse = () => {
        navigate(`/preview/${course.id}`, {
            state: {
                previewContextEntryPoint: `/published-course/${course.id}`,
            },
        });
    };

    const editCourse = () => {
        navigate(`/published-course/${course.id}/edit`);
    };

    return (
        <MainSection>
            <Container>
                <CourseButtonsContainer>
                    <SecondaryButton
                        title={strings.courseEditorPage.previewCourseButtonTitle}
                        aria={strings.courseEditorPage.previewCourseButtonAria}
                        onClick={previewCourse}
                        icon={<VisibilityIcon colour={theme.primaryBrandColour} />}
                    />
                    <SecondaryButton
                        title={strings.coursePage.editButtonTitle}
                        aria={strings.coursePage.editButtonTitleAria}
                        onClick={editCourse}
                        icon={<EditIcon />}
                    />
                    <PrimaryButton
                        title={strings.coursePage.scheduleButtonTitle}
                        aria={strings.coursePage.scheduleButtonTitleAria}
                        onClick={() => {
                            navigate(`/published-course/${course.id}/schedule`, {
                                state: {
                                    backUrl: `/published-course/${course.id}`,
                                },
                            });
                        }}
                        icon={<ScheduleIcon colour={theme.primaryButtonTextColour} />}
                    />
                </CourseButtonsContainer>

                {course.description && (
                    <FlexColMediumGap>
                        <Headline3>{strings.reviewCoursePage.courseDescriptionHeader}</Headline3>
                        <CourseDescriptionCard>{course.description}</CourseDescriptionCard>
                    </FlexColMediumGap>
                )}

                <FlexColMediumGap>
                    <Headline3>{strings.reviewCoursePage.learningObjectivesHeader}</Headline3>
                    <LearningObjectiveContainer>
                        {course.learningObjectives
                            .sort((a, b) => a.order - b.order)
                            .map((x) => (
                                <LearningObjective key={x.id}>
                                    <LearningObjectiveIconContainer>
                                        <CheckIcon />
                                    </LearningObjectiveIconContainer>
                                    <BodyRegular>{x.description}</BodyRegular>
                                </LearningObjective>
                            ))}
                    </LearningObjectiveContainer>
                </FlexColMediumGap>

                <FlexColMediumGap>
                    <Headline3>{strings.reviewCoursePage.modulesHeader}</Headline3>
                    <ModuleAccordionContainer>
                        {course.modules
                            .sort((moduleOne, moduleTwo) => {
                                return moduleOne.order < moduleTwo.order ? -1 : 1;
                            })
                            .map((module) => (
                                <ModuleAccordion key={module.id} module={module} coursePublished={course.published} />
                            ))}
                    </ModuleAccordionContainer>
                </FlexColMediumGap>
            </Container>
        </MainSection>
    );
}

const Container = styled.main`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
    padding: ${sizes.spacingLg} 0;
`;

const CourseButtonsContainer = styled(FlexAlignCenterMediumGap)`
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
    }
`;

const CourseDescriptionCard = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
`;
const ModuleAccordionContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
`;

const LearningObjectiveContainer = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
    width: fit-content;
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
`;

const LearningObjective = styled(FlexAlignCenter)`
    gap: ${sizes.spacingMd};
`;

const LearningObjectiveIconContainer = styled.div`
    padding: ${sizes.spacingMd};
    height: 48px;
    width: 48px;
    border-radius: 50%;
    background-color: ${theme.notificationSuccessBackground};
`;
